// @flow
// Контур для правки формы: цепочка отрезков { type: 'L', p0, p1 } и кривых
// { type: 'C', p0, c1, c2, p1 }. Здесь узлы и ручки, пересечения контуров,
// куски между пересечениями, соединение кусков, объединение и вычитание
// фигур, скругление отдельных углов.
//
// Координаты — единицы плоттера (0,1 мм). Пока идёт правка, они дробные; в
// команды файла уходят округлёнными (pathToCmds).
import { cmdsParts, partsCmds } from './prnModel';

const sub = (a, b) => [a[0] - b[0], a[1] - b[1]];
const add = (a, b) => [a[0] + b[0], a[1] + b[1]];
const mul = (a, k) => [a[0] * k, a[1] * k];
const dot = (a, b) => a[0] * b[0] + a[1] * b[1];
const cross = (a, b) => a[0] * b[1] - a[1] * b[0];
const len = a => Math.hypot(a[0], a[1]);
const lerp = (a, b, t) => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
export const dist = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1]);

const copyPart = p => (p.type === 'L'
  ? { type: 'L', p0: p.p0.slice(), p1: p.p1.slice() }
  : { type: 'C', p0: p.p0.slice(), c1: p.c1.slice(), c2: p.c2.slice(), p1: p.p1.slice() });

// === Контур из команд и обратно ===

// closeGap: зазор в стыке (в единицах), до которого контур считается
// замкнутым; стык закрывается прямой
export function pathFromCmds(cmds: Array<Object>, closeGap: number = 0) {
  const parts = cmdsParts(cmds).map(copyPart);
  if (!parts.length) return { parts, closed: false };
  const first = parts[0].p0;
  const last = parts[parts.length - 1].p1;
  const gap = dist(first, last);
  if (gap === 0) return { parts, closed: true };
  if (gap <= closeGap) return { parts: parts.concat([{ type: 'L', p0: last.slice(), p1: first.slice() }]), closed: true };
  return { parts, closed: false };
}

// Команды файла: точки округлены до целых, стыки общие, пустые отрезки убраны
export function pathToCmds(path: Object) {
  const R = p => [Math.round(p[0]), Math.round(p[1])];
  const out = [];
  let pen = null;
  path.parts.forEach(part => {
    const p0 = pen || R(part.p0);
    const p1 = R(part.p1);
    if (part.type === 'L') {
      if (p0[0] !== p1[0] || p0[1] !== p1[1]) out.push({ type: 'L', p0, p1 });
      else return;
    } else {
      const c1 = R(part.c1);
      const c2 = R(part.c2);
      const all = [p0, c1, c2, p1];
      if (all.every(q => q[0] === p0[0] && q[1] === p0[1])) return;
      out.push({ type: 'C', p0, c1, c2, p1 });
    }
    pen = p1;
  });
  if (path.closed && out.length) {
    const start = out[0].p0;
    const lastPart = out[out.length - 1];
    if (lastPart.p1[0] !== start[0] || lastPart.p1[1] !== start[1]) {
      // стык разошёлся на округлении — свести, иначе закрыть прямой
      if (dist(lastPart.p1, start) <= 1.5) out[out.length - 1] = { ...lastPart, p1: start.slice() };
      else out.push({ type: 'L', p0: lastPart.p1, p1: start.slice() });
    }
  }
  return partsCmds(out);
}

// Узлы: начала отрезков, у открытого контура — ещё его конец
export function pathNodes(path: Object) {
  const nodes = path.parts.map(p => p.p0);
  if (!path.closed && path.parts.length) nodes.push(path.parts[path.parts.length - 1].p1);
  return nodes;
}

// Отрезки, сходящиеся в узле k: входящий и выходящий (null — нет)
function around(path, k) {
  const n = path.parts.length;
  const out = k < n ? k : null;
  let inn = k > 0 ? k - 1 : null;
  if (k === 0 && path.closed) inn = n - 1;
  return { inn, out };
}

// === Точки на отрезке ===

export function pointAt(part: Object, t: number) {
  if (part.type === 'L') return lerp(part.p0, part.p1, t);
  const u = 1 - t;
  const a = u * u * u;
  const b = 3 * u * u * t;
  const c = 3 * u * t * t;
  const d = t * t * t;
  return [
    a * part.p0[0] + b * part.c1[0] + c * part.c2[0] + d * part.p1[0],
    a * part.p0[1] + b * part.c1[1] + c * part.c2[1] + d * part.p1[1],
  ];
}

function derivAt(part, t) {
  if (part.type === 'L') return sub(part.p1, part.p0);
  const u = 1 - t;
  const a = sub(part.c1, part.p0);
  const b = sub(part.c2, part.c1);
  const c = sub(part.p1, part.c2);
  return [
    3 * (a[0] * u * u + 2 * b[0] * u * t + c[0] * t * t),
    3 * (a[1] * u * u + 2 * b[1] * u * t + c[1] * t * t),
  ];
}

// Единичная касательная по ходу контура; у кривой с ручкой в самом узле —
// по соседней точке
function tangentAt(part, t) {
  let d = derivAt(part, t);
  if (len(d) < 1e-9) d = t < 0.5 ? sub(pointAt(part, 0.02), part.p0) : sub(part.p1, pointAt(part, 0.98));
  const l = len(d);
  return l ? mul(d, 1 / l) : [1, 0];
}

export function splitPart(part: Object, t: number) {
  if (part.type === 'L') {
    const m = lerp(part.p0, part.p1, t);
    return [{ type: 'L', p0: part.p0.slice(), p1: m }, { type: 'L', p0: m.slice(), p1: part.p1.slice() }];
  }
  const a = lerp(part.p0, part.c1, t);
  const b = lerp(part.c1, part.c2, t);
  const c = lerp(part.c2, part.p1, t);
  const d = lerp(a, b, t);
  const e = lerp(b, c, t);
  const m = lerp(d, e, t);
  return [
    { type: 'C', p0: part.p0.slice(), c1: a, c2: d, p1: m },
    { type: 'C', p0: m.slice(), c1: e, c2: c, p1: part.p1.slice() },
  ];
}

// Точки отрезка с параметром: прямая — концы, кривая — n кусков
function samples(part, n = 24) {
  const k = part.type === 'L' ? 1 : n;
  const out = [];
  for (let i = 0; i <= k; i += 1) out.push({ t: i / k, p: pointAt(part, i / k) });
  return out;
}

const LEN_SAMPLES = 48;

export function partLength(part: Object) {
  if (part.type === 'L') return dist(part.p0, part.p1);
  const s = samples(part, LEN_SAMPLES);
  let l = 0;
  for (let i = 1; i < s.length; i += 1) l += dist(s[i - 1].p, s[i].p);
  return l;
}

// Параметр, на котором от начала отрезка пройдено расстояние d
function tAtLength(part, d) {
  if (part.type === 'L') {
    const l = dist(part.p0, part.p1);
    return l ? Math.min(1, d / l) : 0;
  }
  const s = samples(part, LEN_SAMPLES);
  let acc = 0;
  for (let i = 1; i < s.length; i += 1) {
    const step = dist(s[i - 1].p, s[i].p);
    if (acc + step >= d) return s[i - 1].t + ((s[i].t - s[i - 1].t) * (d - acc)) / (step || 1);
    acc += step;
  }
  return 1;
}

function partBox(part) {
  const pts = part.type === 'L' ? [part.p0, part.p1] : [part.p0, part.c1, part.c2, part.p1];
  return {
    minX: Math.min(...pts.map(p => p[0])),
    minY: Math.min(...pts.map(p => p[1])),
    maxX: Math.max(...pts.map(p => p[0])),
    maxY: Math.max(...pts.map(p => p[1])),
  };
}

const boxesMeet = (a, b, pad = 0) => a.minX <= b.maxX + pad && b.minX <= a.maxX + pad && a.minY <= b.maxY + pad && b.minY <= a.maxY + pad;

// Ломаная контура (для проверки «внутри» и расстояний)
export function flattenPath(path: Object, perCurve: number = 16) {
  const out = [];
  path.parts.forEach((part, i) => {
    samples(part, perCurve).forEach((s, j) => {
      if (i === 0 || j > 0) out.push(s.p);
    });
  });
  return out;
}

// Точка внутри многоугольника (правило чёт-нечет)
export function insidePolygon(q: Array<number>, poly: Array<Array<number>>) {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i, i += 1) {
    const a = poly[i];
    const b = poly[j];
    if ((a[1] > q[1]) !== (b[1] > q[1]) && q[0] < ((b[0] - a[0]) * (q[1] - a[1])) / (b[1] - a[1]) + a[0]) inside = !inside;
  }
  return inside;
}

// Ближайшая к q точка контура: номер отрезка, параметр, расстояние
export function nearestOnPath(path: Object, q: Array<number>) {
  let best = null;
  path.parts.forEach((part, i) => {
    let t;
    if (part.type === 'L') {
      const d = sub(part.p1, part.p0);
      const l2 = dot(d, d);
      t = l2 ? Math.max(0, Math.min(1, dot(sub(q, part.p0), d) / l2)) : 0;
    } else {
      let bestT = 0;
      let bestD = Infinity;
      samples(part, 32).forEach(s => {
        const dd = dist(s.p, q);
        if (dd < bestD) { bestD = dd; bestT = s.t; }
      });
      t = bestT;
      // уточнить: минимум расстояния, f(t) = (B(t) − q)·B'(t) = 0
      for (let k = 0; k < 6; k += 1) {
        const p = pointAt(part, t);
        const d1 = derivAt(part, t);
        const h = 1e-4;
        const d2 = sub(derivAt(part, Math.min(1, t + h)), derivAt(part, Math.max(0, t - h)));
        const f = dot(sub(p, q), d1);
        const df = dot(d1, d1) + dot(sub(p, q), mul(d2, 1 / (2 * h)));
        if (!df) break;
        t = Math.max(0, Math.min(1, t - f / df));
      }
    }
    const p = pointAt(part, t);
    const d = dist(p, q);
    if (!best || d < best.d) best = { i, t, d, point: p };
  });
  return best;
}

// === Пересечения ===

function segCross(a0, a1, b0, b1) {
  const r = sub(a1, a0);
  const s = sub(b1, b0);
  const den = cross(r, s);
  if (Math.abs(den) < 1e-12) return null;
  const qp = sub(b0, a0);
  const t = cross(qp, s) / den;
  const u = cross(qp, r) / den;
  const eps = 1e-9;
  if (t < -eps || t > 1 + eps || u < -eps || u > 1 + eps) return null;
  return { t: Math.max(0, Math.min(1, t)), u: Math.max(0, Math.min(1, u)) };
}

// Уточнить точку пересечения методом Ньютона: A(ta) = B(tb)
function refine(A, B, ta0, tb0) {
  let ta = ta0;
  let tb = tb0;
  for (let k = 0; k < 12; k += 1) {
    const f = sub(pointAt(A, ta), pointAt(B, tb));
    if (len(f) < 1e-9) break;
    const da = derivAt(A, ta);
    const db = mul(derivAt(B, tb), -1);
    const det = cross(da, db);
    if (Math.abs(det) < 1e-12) break;
    // [da db] · [dta dtb]^T = −f
    const dta = cross(mul(f, -1), db) / det;
    const dtb = cross(da, mul(f, -1)) / det;
    ta = Math.max(0, Math.min(1, ta + dta));
    tb = Math.max(0, Math.min(1, tb + dtb));
  }
  return { ta, tb };
}

// Пересечения двух отрезков: [{ ta, tb, p }]
export function intersectParts(A: Object, B: Object) {
  if (!boxesMeet(partBox(A), partBox(B), 1e-6)) return [];
  const sa = samples(A);
  const sb = samples(B);
  const hits = [];
  for (let i = 1; i < sa.length; i += 1) {
    for (let j = 1; j < sb.length; j += 1) {
      const c = segCross(sa[i - 1].p, sa[i].p, sb[j - 1].p, sb[j].p);
      if (c) {
        let ta = sa[i - 1].t + (sa[i].t - sa[i - 1].t) * c.t;
        let tb = sb[j - 1].t + (sb[j].t - sb[j - 1].t) * c.u;
        if (A.type === 'C' || B.type === 'C') ({ ta, tb } = refine(A, B, ta, tb));
        const pa = pointAt(A, ta);
        if (dist(pa, pointAt(B, tb)) < 0.01 && !hits.some(h => dist(h.p, pa) < 1e-3)) hits.push({ ta, tb, p: pa });
      }
    }
  }
  return hits;
}

// Место на контуре: номер отрезка + параметр; конец отрезка — это начало
// следующего, конец замкнутого — его начало
function normG(g, path) {
  let v = g - Math.floor(g) > 1 - 1e-9 ? Math.ceil(g) : g;
  if (path.closed && v >= path.parts.length - 1e-9) v = 0;
  return v;
}

// Пересечения контуров: [{ ga, gb, p }]. Одно место на обоих контурах —
// одно пересечение (узел на стыке двух отрезков найдётся дважды)
export function intersectPaths(PA: Object, PB: Object) {
  const hits = [];
  PA.parts.forEach((A, i) => {
    PB.parts.forEach((B, j) => {
      intersectParts(A, B).forEach(h => {
        const ga = normG(i + h.ta, PA);
        const gb = normG(j + h.tb, PB);
        if (!hits.some(x => Math.abs(x.ga - ga) < 1e-6 && Math.abs(x.gb - gb) < 1e-6)) hits.push({ ga, gb, p: h.p });
      });
    });
  });
  return hits;
}

// Проходы контура: у части лекал нож обходит контур дважды (в файле два
// круга подряд). Возвращает круги по отдельности; обычный контур — один
export function loopsOf(path: Object) {
  if (!path.closed) return [path];
  const start = path.parts[0].p0;
  const loops = [];
  let from = 0;
  path.parts.forEach((p, i) => {
    if (i < path.parts.length - 1 && dist(p.p1, start) < 1e-6) {
      loops.push({ parts: path.parts.slice(from, i + 1), closed: true });
      from = i + 1;
    }
  });
  if (!loops.length) return [path];
  loops.push({ parts: path.parts.slice(from), closed: true });
  return loops;
}

function repeatPath(path, k) {
  let parts = [];
  for (let i = 0; i < k; i += 1) parts = parts.concat(path.parts.map(copyPart));
  return { parts, closed: path.closed };
}

// === Куски между разрезами ===

// Разрезать контур в точках cuts ([{ g, p? }]; p — общая точка разреза,
// чтобы у соседних контуров концы кусков совпали до долей единицы).
// Возвращает куски по ходу контура; у замкнутого первый кусок начинается
// в первом разрезе.
export function splitPathAt(path: Object, cuts: Array<Object>) {
  const n = path.parts.length;
  // разрез у самого конца отрезка — это разрез в следующем узле
  const whole = g => (g - Math.floor(g) > 1 - 1e-9 ? Math.ceil(g) : g);
  let list = cuts
    .map(c => ({ ...c, g: whole(Math.max(0, Math.min(n, c.g))) }))
    .map(c => (path.closed && c.g >= n - 1e-9 ? { ...c, g: 0 } : c))
    .filter(c => path.closed || (c.g > 1e-9 && c.g < n - 1e-9))
    .sort((a, b) => a.g - b.g);
  list = list.filter((c, i) => i === 0 || c.g - list[i - 1].g > 1e-7);
  if (!list.length) return [{ parts: path.parts.map(copyPart), closed: path.closed }];

  const out = [];
  const starts = [];
  let ci = 0;
  path.parts.forEach((part, i) => {
    let rest = copyPart(part);
    let prevT = 0;
    while (ci < list.length && list[ci].g < i + 1 - 1e-9) {
      const c = list[ci];
      ci += 1;
      const t = c.g - i;
      if (t <= 1e-9) {
        starts.push(out.length);
      } else {
        const [a, b] = splitPart(rest, (t - prevT) / (1 - prevT));
        if (c.p) {
          a.p1 = c.p.slice();
          b.p0 = c.p.slice();
        }
        out.push(a);
        starts.push(out.length);
        rest = b;
        prevT = t;
      }
    }
    out.push(rest);
  });

  const pieces = [];
  if (path.closed) {
    for (let j = 0; j < starts.length; j += 1) {
      const from = starts[j];
      const to = j + 1 < starts.length ? starts[j + 1] : starts[0] + out.length;
      const parts = [];
      for (let k = from; k < to; k += 1) parts.push(out[k % out.length]);
      if (parts.length) pieces.push({ parts, closed: false });
    }
  } else {
    const bounds = [0].concat(starts, [out.length]);
    for (let j = 1; j < bounds.length; j += 1) {
      const parts = out.slice(bounds[j - 1], bounds[j]);
      if (parts.length) pieces.push({ parts, closed: false });
    }
  }
  return pieces;
}

function concatPaths(a, b) {
  if (!a.parts.length) return { parts: b.parts.slice(), closed: false };
  if (!b.parts.length) return { parts: a.parts.slice(), closed: false };
  const joint = a.parts[a.parts.length - 1].p1;
  const head = { ...b.parts[0], p0: joint.slice() };
  return { parts: a.parts.concat([head], b.parts.slice(1)), closed: false };
}

export function reversePath(path: Object) {
  return {
    closed: path.closed,
    parts: path.parts.slice().reverse().map(p => (p.type === 'L'
      ? { type: 'L', p0: p.p1.slice(), p1: p.p0.slice() }
      : { type: 'C', p0: p.p1.slice(), c1: p.c2.slice(), c2: p.c1.slice(), p1: p.p0.slice() })),
  };
}

function closeExact(path) {
  const parts = path.parts.slice();
  const last = parts[parts.length - 1];
  parts[parts.length - 1] = { ...last, p1: parts[0].p0.slice() };
  return { parts, closed: true };
}

// Убрать кусок j: что остаётся от контура (открытые линии)
export function withoutPiece(pieces: Array<Object>, j: number, closed: boolean) {
  const glue = list => list.reduce((acc, p) => concatPaths(acc, p), { parts: [], closed: false });
  if (closed) {
    const rest = pieces.slice(j + 1).concat(pieces.slice(0, j));
    return rest.length ? [glue(rest)] : [];
  }
  return [glue(pieces.slice(0, j)), glue(pieces.slice(j + 1))].filter(p => p.parts.length);
}

// Сцепить открытые линии, у которых концы сошлись в одной из точек seeds
// (там, где только что резали). entries: [{ path, keys }]; у сцепленных
// keys объединяются. Сошлись концы одной линии — она замыкается.
export function joinAt(entries: Array<Object>, seeds: Array<Array<number>>, tol: number = 1) {
  const list = entries.map(e => ({ path: e.path, keys: e.keys.slice() }));
  const nearSeed = p => seeds.some(s => dist(s, p) <= tol);
  const first = e => e.path.parts[0].p0;
  const last = e => e.path.parts[e.path.parts.length - 1].p1;
  const open = e => !e.path.closed && e.path.parts.length > 0;
  let changed = true;
  while (changed) {
    changed = false;
    for (let i = 0; i < list.length; i += 1) {
      const e = list[i];
      if (open(e) && dist(first(e), last(e)) <= tol && nearSeed(last(e))) {
        e.path = closeExact(e.path);
        changed = true;
      }
    }
    for (let a = 0; a < list.length && !changed; a += 1) {
      for (let b = 0; b < list.length && !changed; b += 1) {
        const A = list[a];
        const B = list[b];
        if (a !== b && open(A) && open(B)) {
          let merged = null;
          if (dist(last(A), first(B)) <= tol && nearSeed(last(A))) merged = concatPaths(A.path, B.path);
          else if (dist(last(A), last(B)) <= tol && nearSeed(last(A))) merged = concatPaths(A.path, reversePath(B.path));
          else if (dist(first(A), first(B)) <= tol && nearSeed(first(A))) merged = concatPaths(reversePath(A.path), B.path);
          if (merged) {
            A.path = merged;
            A.keys = A.keys.concat(B.keys);
            list.splice(b, 1);
            changed = true;
          }
        }
      }
    }
  }
  return list;
}

// === Объединить, вычесть, пересечь ===
// Два замкнутых контура режутся друг о друга; куски, что снаружи или внутри
// другого, собираются обратно в замкнутые контуры.
// Главный круг контура и сколько раз нож его проходит. Крошечные петли
// (узел туда-обратно) — не проход
function mainLoop(path) {
  const loops = loopsOf(path);
  if (loops.length === 1) return { loop: path, times: 1 };
  const lengths = loops.map(l => l.parts.reduce((sum, p) => sum + partLength(p), 0));
  const longest = Math.max(...lengths);
  const main = loops[lengths.indexOf(longest)];
  return { loop: main, times: lengths.filter(l => l >= longest * 0.9).length };
}

export function booleanPaths(A0: Object, B0: Object, op: 'union' | 'difference' | 'intersection') {
  // двойной рез: считаем по одному кругу, результат режется столько же раз
  const a = mainLoop(A0);
  const b = mainLoop(B0);
  if (a.loop !== A0 || b.loop !== B0) {
    const times = Math.max(a.times, b.times);
    return booleanPaths(a.loop, b.loop, op).map(p => (times > 1 ? repeatPath(p, times) : p));
  }
  const A = A0;
  const B = B0;
  const polyA = flattenPath(A);
  const polyB = flattenPath(B);
  // два пересечения ближе 0,05 мм — одно (стык отрезков чуть разошёлся)
  const hits = intersectPaths(A, B).filter((h, i, all) => !all.slice(0, i).some(x => dist(x.p, h.p) < 0.5));
  const midOf = piece => {
    const total = piece.parts.reduce((s, p) => s + partLength(p), 0);
    let acc = 0;
    for (let i = 0; i < piece.parts.length; i += 1) {
      const l = partLength(piece.parts[i]);
      if (acc + l >= total / 2) return pointAt(piece.parts[i], tAtLength(piece.parts[i], total / 2 - acc));
      acc += l;
    }
    return piece.parts[0].p0;
  };
  if (hits.length < 2) {
    const aInB = insidePolygon(midOf(A), polyB);
    const bInA = insidePolygon(midOf(B), polyA);
    if (op === 'union') return aInB ? [B] : bInA ? [A] : [A, B];
    if (op === 'difference') return bInA ? [A, B] : aInB ? [] : [A];
    return aInB ? [A] : bInA ? [B] : [];
  }
  const piecesA = splitPathAt(A, hits.map(h => ({ g: h.ga, p: h.p })));
  const piecesB = splitPathAt(B, hits.map(h => ({ g: h.gb, p: h.p })));
  const keepA = piecesA.filter(p => insidePolygon(midOf(p), polyB) === (op === 'intersection'));
  const keepB = piecesB.filter(p => insidePolygon(midOf(p), polyA) === (op !== 'union'));
  const seeds = hits.map(h => h.p);
  return joinAt(keepA.concat(keepB).map(path => ({ path, keys: [] })), seeds, 1e-3)
    .map(e => e.path)
    .filter(p => p.closed);
}

// === Узлы: сдвиг, ручки, удаление, добавление, прямая и кривая ===

export function moveNodes(path: Object, idxs: Array<number>, d: Array<number>) {
  const parts = path.parts.map(copyPart);
  idxs.forEach(k => {
    const { inn, out } = around(path, k);
    if (out !== null) {
      parts[out].p0 = add(parts[out].p0, d);
      if (parts[out].type === 'C') parts[out].c1 = add(parts[out].c1, d);
    }
    if (inn !== null) {
      parts[inn].p1 = add(parts[inn].p1, d);
      if (parts[inn].type === 'C') parts[inn].c2 = add(parts[inn].c2, d);
    }
  });
  return { parts, closed: path.closed };
}

export function moveHandle(path: Object, i: number, which: 'c1' | 'c2', q: Array<number>) {
  const parts = path.parts.map(copyPart);
  if (parts[i] && parts[i].type === 'C') parts[i][which] = q.slice();
  return { parts, closed: path.closed };
}

// Удалить узлы. Соседние отрезки сливаются: две прямые — в прямую, иначе в
// кривую с внешними ручками. Конец открытой линии уходит вместе с отрезком.
export function deleteNodes(path: Object, idxs: Array<number>) {
  let cur = { parts: path.parts.map(copyPart), closed: path.closed };
  const sorted = Array.from(new Set(idxs)).sort((a, b) => b - a);
  for (let s = 0; s < sorted.length; s += 1) {
    const k = sorted[s];
    const n = cur.parts.length;
    if (n <= (cur.closed ? 2 : 1)) return null; // от контура ничего не останется
    const { inn, out } = around(cur, k);
    if (inn === null) cur = { ...cur, parts: cur.parts.slice(1) };
    else if (out === null) cur = { ...cur, parts: cur.parts.slice(0, -1) };
    else {
      const A = cur.parts[inn];
      const B = cur.parts[out];
      const merged = A.type === 'L' && B.type === 'L'
        ? { type: 'L', p0: A.p0, p1: B.p1 }
        : {
          type: 'C',
          p0: A.p0,
          c1: A.type === 'C' ? A.c1 : lerp(A.p0, A.p1, 1 / 3),
          c2: B.type === 'C' ? B.c2 : lerp(B.p0, B.p1, 2 / 3),
          p1: B.p1,
        };
      const parts = cur.parts.slice();
      if (out === 0) {
        // узел 0 замкнутого контура: слияние последнего и первого
        parts[0] = merged;
        parts.pop();
      } else {
        parts.splice(inn, 2, merged);
      }
      cur = { ...cur, parts };
    }
  }
  return cur;
}

// Новый узел в точке g (номер отрезка + параметр). Возвращает контур и номер узла
export function insertNode(path: Object, g: number) {
  const i = Math.min(path.parts.length - 1, Math.floor(g));
  const t = g - i;
  if (t <= 1e-6 || t >= 1 - 1e-6) return { path, node: t >= 1 - 1e-6 ? i + 1 : i };
  const parts = path.parts.map(copyPart);
  parts.splice(i, 1, ...splitPart(parts[i], t));
  return { path: { parts, closed: path.closed }, node: i + 1 };
}

// Отрезок i — в кривую (ручки на трети длины) или в прямую
export function setPartType(path: Object, i: number, type: 'L' | 'C') {
  const parts = path.parts.map(copyPart);
  const p = parts[i];
  if (!p || p.type === type) return path;
  parts[i] = type === 'C'
    ? { type: 'C', p0: p.p0, c1: lerp(p.p0, p.p1, 1 / 3), c2: lerp(p.p0, p.p1, 2 / 3), p1: p.p1 }
    : { type: 'L', p0: p.p0, p1: p.p1 };
  return { parts, closed: path.closed };
}

// === Скругление отдельных углов ===

// Угол в узле k: поворот и наибольший радиус (скругление не длиннее
// половины каждого из соседних отрезков). null — не угол (конец или гладко)
export function cornerAt(path: Object, k: number) {
  const { inn, out } = around(path, k);
  if (inn === null || out === null) return null;
  const A = path.parts[inn];
  const B = path.parts[out];
  const uA = tangentAt(A, 1);
  const uB = tangentAt(B, 0);
  const phi = Math.acos(Math.max(-1, Math.min(1, dot(uA, uB))));
  if (phi < Math.PI / 180 || phi > (Math.PI * 179) / 180) return null;
  const dMax = Math.min(partLength(A), partLength(B)) / 2;
  return { phi, maxR: dMax / Math.tan(phi / 2) };
}

// Скруглить углы в узлах idxs радиусом r. Угол со стороны кривой режется
// по самой кривой, дуга встаёт по касательным
export function filletNodes(path: Object, idxs: Array<number>, r: number) {
  if (!(r > 0)) return path;
  let parts = path.parts.map(copyPart);
  const cur = () => ({ parts, closed: path.closed });
  const sorted = Array.from(new Set(idxs)).sort((a, b) => b - a);
  sorted.forEach(k => {
    const c = cornerAt(cur(), k);
    if (!c) return;
    const { inn, out } = around(cur(), k);
    const A = parts[inn];
    const B = parts[out];
    const tanHalf = Math.tan(c.phi / 2);
    const d = Math.min(r * tanHalf, Math.min(partLength(A), partLength(B)) / 2);
    const rr = d / tanHalf;
    const kk = (4 / 3) * Math.tan(c.phi / 4) * rr;
    const ta = tAtLength(A, partLength(A) - d);
    const tb = tAtLength(B, d);
    const aKeep = splitPart(A, ta)[0];
    const bKeep = splitPart(B, tb)[1];
    const T1 = aKeep.p1;
    const T2 = bKeep.p0;
    const u1 = tangentAt(A, ta);
    const u2 = tangentAt(B, tb);
    const arc = { type: 'C', p0: T1.slice(), c1: add(T1, mul(u1, kk)), c2: sub(T2, mul(u2, kk)), p1: T2.slice() };
    const next = parts.slice();
    next[inn] = aKeep;
    next[out] = bKeep;
    if (out === 0) next.push(arc); // угол в начале замкнутого: дуга в конце
    else next.splice(out, 0, arc);
    parts = next;
  });
  return cur();
}
